import { BigBashEvent, GamePoint, PointCalculationEnum, RoundPoint, RoundPointsFieldEnum } from "@/types/types";
import { EntityTypeEnum, idTimestampManager } from "@/utils/IDTimestampManager";

class PointsCalculatorInputChangeHandler {
	private pointCalculationType: PointCalculationEnum;

	constructor(pointCalculationType: PointCalculationEnum = PointCalculationEnum.mostPoints) {
		this.pointCalculationType = pointCalculationType;
	}

	updateEvent(event: BigBashEvent, gameId: string, teamId: string, name: string, value: string): BigBashEvent {
		const updatedEvent = { ...event };
		const updatedGamePoints = this._handleRoundValueChange(
			updatedEvent,
			gameId,
			teamId,
			name,
			value
		);
		updatedEvent.game_points = updatedGamePoints;
		return updatedEvent;
	}

	getRoundNumberFromRoundName(roundName: string): number {
		return this._extractRoundNumberFromRoundName(roundName);
	}

	findExistingRoundPoint(event: BigBashEvent, gameId: string, teamId: string, roundNumber: number): RoundPoint | undefined {
		const gamePoints = this._initializeGamePoints(event);
		const gamePoint = this._findGamePointsForTeam(gamePoints, teamId, gameId);
		return gamePoint?.rounds.find(roundPoint => roundPoint.round === roundNumber);
	}

	private _handleRoundValueChange(event: BigBashEvent, gameId: string, teamId: string, roundName: string, rawValue: string): Array<GamePoint> {
		const roundNumber = this._extractRoundNumberFromRoundName(roundName);
		const value = this._parseValue(rawValue);
		const gamePoints = this._initializeGamePoints(event);

		return this._updateGamePointsWithRoundData(gamePoints, teamId, gameId, roundNumber, value);
	}

	private _parseValue(value: string): number {
		if (this.pointCalculationType === PointCalculationEnum.mostPoints) {
			return parseInt(value, 10) || 0;
		}
		return parseFloat(value) || 0;
	}

	getFieldForCalculationType(): RoundPointsFieldEnum {
		switch (this.pointCalculationType) {
			case PointCalculationEnum.smallestDifference:
				return RoundPointsFieldEnum.teamSmallestDifferenceValue;
			case PointCalculationEnum.fastestTime:
				return RoundPointsFieldEnum.teamFastestTimeSeconds;
			case PointCalculationEnum.mostPoints:
			default:
				return RoundPointsFieldEnum.points;
		}
	}

	private _updateGamePointsWithRoundData(gamePoints: Array<GamePoint>, teamId: string, gameId: string, roundNumber: number, value: number): Array<GamePoint> {
		const existingGamePoint = this._findGamePointsForTeam(gamePoints, teamId, gameId);
		const updatedGamePoint = existingGamePoint
			? this._updateExistingGamePoints(existingGamePoint, roundNumber, value)
			: this._createGamePointsWithRound(teamId, gameId, roundNumber, value);

		return this._replaceOrAddGamePoints(gamePoints, updatedGamePoint, teamId, gameId);
	}

	private _updateExistingGamePoints(gamePoint: GamePoint, roundNumber: number, value: number): GamePoint {
		const updatedRounds = this._updateRoundsWithNewValue(gamePoint.rounds, roundNumber, value);

		return {
			...gamePoint,
			rounds: updatedRounds,
			updated_at: new Date()
		};
	}

	private _createGamePointsWithRound(teamId: string, gameId: string, roundNumber: number, value: number): GamePoint {
		const newRoundPoints = this._createNewRoundPoints(roundNumber, value);
		return {
			...this._createNewGamePoints(teamId, gameId),
			rounds: [newRoundPoints]
		};
	}

	private _updateRoundsWithNewValue(rounds: Array<RoundPoint>, roundNumber: number, value: number): Array<RoundPoint> {
		const existingRoundIndex = rounds.findIndex(rp => rp.round === roundNumber);

		if (existingRoundIndex >= 0) {
			return this._updateExistingRound(rounds, existingRoundIndex, value);
		} else {
			return this._addNewRound(rounds, roundNumber, value);
		}
	}

	private _updateExistingRound(rounds: Array<RoundPoint>, roundIndex: number, value: number): Array<RoundPoint> {
		const updatedRounds = [...rounds];
		const field = this.getFieldForCalculationType();
		updatedRounds[roundIndex] = {
			...updatedRounds[roundIndex],
			[field]: value,
			updated_at: new Date()
		};
		return updatedRounds;
	}

	private _addNewRound(rounds: Array<RoundPoint>, roundNumber: number, value: number): Array<RoundPoint> {
		const newRoundPoints = this._createNewRoundPoints(roundNumber, value);
		return [...rounds, newRoundPoints];
	}

	private _replaceOrAddGamePoints(gamePoints: Array<GamePoint>, updatedGamePoint: GamePoint, teamId: string, gameId: string): Array<GamePoint> {
		const existingIndex = gamePoints.findIndex(gp => gp.team === teamId && gp.game === gameId);

		if (existingIndex >= 0) {
			const updatedGamePoints = [...gamePoints];
			updatedGamePoints[existingIndex] = updatedGamePoint;
			return updatedGamePoints;
		} else {
			return [...gamePoints, updatedGamePoint];
		}
	}

	private _extractRoundNumberFromRoundName(roundName: string): number {
		const roundMatch = roundName.match(/round-(\d+)/);
		if (!roundMatch) {
			throw new Error("Invalid round name format");
		}
		return parseInt(roundMatch[1], 10);
	}

	private _initializeGamePoints(event: BigBashEvent): Array<GamePoint> {
		return event.game_points || [];
	}

	private _findGamePointsForTeam(gamePoints: Array<GamePoint>, teamId: string, gameId: string): GamePoint | undefined {
		return gamePoints.find(gp => gp.team === teamId && gp.game === gameId);
	}

	private _createNewGamePoints(teamId: string, gameId: string): GamePoint {
		return {
			...idTimestampManager.setInitialValues(EntityTypeEnum.GamePoint),
			game: gameId,
			team: teamId,
			rounds: [],
		};
	}

	private _createNewRoundPoints(roundNumber: number, value: number): RoundPoint {
		const field = this.getFieldForCalculationType();
		return {
			...idTimestampManager.setInitialValues(EntityTypeEnum.RoundPoint),
			points: null,
			team_fastest_time_seconds: null,
			team_smallest_difference_value: null,
			round_target_value: null,
			[field]: value,
			round: roundNumber
		};
	}
}

export default PointsCalculatorInputChangeHandler;